// §7.6.21 FaxImportLogsPage — attachment import log viewer for a specific inbox config.
//
// Route: /admin/fax-import-logs/:configId
// Roles: medflow_admin

import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api, ApiError } from '../../services/api'
import {
  PageHeader,
  DataTable,
  Alert,
  StatusBadge,
  ColumnSettingsButton,
  btnSecondary,
  btnDanger,
  colors,
  input,
  label as labelStyle,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useTableColumns } from '../../hooks/use-table-columns'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ParsedData {
  readonly firstName?: string
  readonly lastName?: string
  readonly [key: string]: unknown
}

interface AttachmentRow {
  readonly id: string
  readonly receivedAt: string
  readonly filename: string | null
  readonly parsedData: ParsedData | null
  readonly status: string
  readonly orderId: string | null
  readonly failureReason: string | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTs(ts: string): string {
  return new Date(ts).toLocaleString()
}

function patientName(row: AttachmentRow): string {
  const d = row.parsedData
  if (!d) return '—'
  const first = d.firstName ?? ''
  const last = d.lastName ?? ''
  if (!first && !last) return '—'
  return `${last}${last && first ? ', ' : ''}${first}`
}

// Map attachment statuses to StatusBadge-compatible keys
function statusBadgeKey(status: string): string {
  switch (status) {
    case 'pending':
      return 'pending'
    case 'processing':
      return 'in_progress'
    case 'parsed':
      return 'resulted'
    case 'order_created':
      return 'completed'
    case 'failed':
      return 'retired'
    default:
      return 'unknown'
  }
}

// ---------------------------------------------------------------------------
// FaxImportLogsPage
// ---------------------------------------------------------------------------

export function FaxImportLogsPage() {
  const { configId } = useParams<{ configId?: string }>()

  const [rows, setRows] = useState<readonly AttachmentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const limit = 50

  const { tableId: _, ...tableColumnProps } = useTableColumns('fax-import-logs')

  const refresh = useCallback(async () => {
    if (!configId) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) })
      if (statusFilter) params.set('status', statusFilter)
      const res = await api.get<{ rows: AttachmentRow[]; total: number }>(
        `/admin/fax/inbox-configs/${configId}/logs?${params.toString()}`,
      )
      setRows(res.data?.rows ?? [])
      setTotal(res.data?.total ?? 0)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load import logs')
    } finally {
      setLoading(false)
    }
  }, [configId, page, statusFilter])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function handleRetry(row: AttachmentRow) {
    setError(null)
    try {
      await api.post(`/admin/fax/attachments/${row.id}/retry`, {})
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Retry failed')
    }
  }

  const columns: readonly Column<AttachmentRow>[] = [
    {
      key: 'receivedAt',
      header: 'Received',
      sortable: true,
      render: (r) => formatTs(r.receivedAt),
    },
    {
      key: 'filename',
      header: 'Filename',
      render: (r) => r.filename ?? '—',
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (r) => patientName(r),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (r) => <StatusBadge status={statusBadgeKey(r.status)} label={r.status.replace(/_/g, ' ')} />,
    },
    {
      key: 'orderId',
      header: 'Order',
      render: (r) =>
        r.orderId ? (
          <Link to={`/orders/${r.orderId}`} style={{ color: colors.accent, textDecoration: 'underline' }}>
            View Order
          </Link>
        ) : (
          '—'
        ),
    },
    {
      key: 'failureReason',
      header: 'Failure Reason',
      render: (r) => {
        if (!r.failureReason) return null
        return (
          <span
            title={r.failureReason}
            style={{
              display: 'inline-block',
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: colors.danger,
              fontSize: '0.8125rem',
            }}
          >
            {r.failureReason}
          </span>
        )
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) =>
        r.status === 'failed' ? (
          <button
            type="button"
            style={{ ...btnDanger, fontSize: '0.75rem', padding: '2px 8px' }}
            onClick={(e) => {
              e.stopPropagation()
              void handleRetry(r)
            }}
          >
            Retry
          </button>
        ) : null,
    },
  ]

  const totalPages = Math.max(1, Math.ceil(total / limit))

  return (
    <div>
      <PageHeader
        title="Fax Import Logs"
        subtitle={configId ? `Inbox config: ${configId}` : 'All inboxes'}
        backTo="/admin/fax-config"
        backLabel="Fax Config"
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div>
              <label htmlFor="statusFilter" style={{ ...labelStyle, display: 'inline', marginRight: '0.5rem' }}>
                Status:
              </label>
              <select
                id="statusFilter"
                style={{ ...input, width: 'auto', display: 'inline-block' }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setPage(1)
                }}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="parsed">Parsed</option>
                <option value="order_created">Order Created</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" style={btnSecondary} onClick={() => void refresh()}>
              Refresh
            </button>
          </div>
        }
      />

      {error && <Alert type="error" message={error} />}

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows as AttachmentRow[]}
          keyField="id"
          emptyMessage="No import log entries found."
          {...tableColumnProps}
        />
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'center' }}>
          <button
            type="button"
            style={btnSecondary}
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span style={{ color: colors.textMuted, fontSize: '0.875rem' }}>
            Page {page} of {totalPages} ({total} total)
          </span>
          <button
            type="button"
            style={btnSecondary}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
